import React, { useState, useEffect } from 'react';
import { Table, Button, Input, Select, DatePicker, Space, Tag, Modal, message, Form, Radio, InputNumber, Popconfirm } from 'antd';
import { SearchOutlined, UserOutlined, LockOutlined, UnlockOutlined, DeleteOutlined } from '@ant-design/icons';
import { getUserList, getUserDetail, banUser, unbanUser, deleteUser } from '../api/users';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;

function Users() {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [filters, setFilters] = useState({
    keyword: '',
    status: '',
    dateRange: null
  });

  // 详情弹窗
  const [detailVisible, setDetailVisible] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // 封禁弹窗
  const [banVisible, setBanVisible] = useState(false);
  const [banForm] = Form.useForm();
  const [banLoading, setBanLoading] = useState(false);
  const [banUserId, setBanUserId] = useState(null);

  // 加载用户列表
  const loadUsers = async (page = 1) => {
    setLoading(true);
    try {
      const params = {
        page,
        pageSize: pagination.pageSize,
        keyword: filters.keyword || undefined,
        status: filters.status || undefined,
        startDate: filters.dateRange ? filters.dateRange[0].format('YYYY-MM-DD') : undefined,
        endDate: filters.dateRange ? filters.dateRange[1].format('YYYY-MM-DD') : undefined
      };

      const res = await getUserList(params);
      if (res.success) {
        setUsers(res.data.list);
        setPagination({
          ...pagination,
          current: page,
          total: res.data.pagination.total
        });
      }
    } catch (error) {
      message.error('加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers(1);
  }, []);

  // 搜索
  const handleSearch = () => {
    loadUsers(1);
  };

  // 重置
  const handleReset = () => {
    setFilters({ keyword: '', status: '', dateRange: null });
    setPagination({ ...pagination, current: 1 });
    setTimeout(() => loadUsers(1), 0);
  };

  // 查看详情
  const handleViewDetail = async (userId) => {
    setDetailVisible(true);
    setDetailLoading(true);
    try {
      const res = await getUserDetail(userId);
      if (res.success) {
        setCurrentUser(res.data);
      }
    } catch (error) {
      message.error('加载详情失败');
    } finally {
      setDetailLoading(false);
    }
  };

  // 打开封禁弹窗
  const handleOpenBan = (userId) => {
    setBanUserId(userId);
    setBanVisible(true);
    banForm.setFieldsValue({
      banType: 'temporary',
      banDays: 7,
      reason: ''
    });
  };

  // 封禁用户
  const handleBan = async () => {
    try {
      const values = await banForm.validateFields();
      setBanLoading(true);
      
      const res = await banUser(banUserId, values);
      if (res.success) {
        message.success('封禁成功');
        setBanVisible(false);
        banForm.resetFields();
        loadUsers(pagination.current);
      } else {
        message.error(res.message || '封禁失败');
      }
    } catch (error) {
      if (error.errorFields) {
        message.error('请填写完整信息');
      } else {
        message.error('封禁失败');
      }
    } finally {
      setBanLoading(false);
    }
  };

  // 解封用户
  const handleUnban = async (userId) => {
    try {
      const res = await unbanUser(userId);
      if (res.success) {
        message.success('解封成功');
        loadUsers(pagination.current);
      } else {
        message.error(res.message || '解封失败');
      }
    } catch (error) {
      message.error('解封失败');
    }
  };

  // 删除用户
  const handleDelete = async (userId) => {
    try {
      const res = await deleteUser(userId);
      if (res.success) {
        message.success('删除成功');
        loadUsers(pagination.current);
      } else {
        message.error(res.message || '删除失败');
      }
    } catch (error) {
      message.error('删除失败');
    }
  };

  // 状态标签
  const getStatusTag = (status) => {
    const statusMap = {
      normal: { color: 'success', text: '正常' },
      banned: { color: 'error', text: '已封禁' },
      deleted: { color: 'default', text: '已删除' }
    };
    const config = statusMap[status] || statusMap.normal;
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 80
    },
    {
      title: '用户信息',
      key: 'userInfo',
      width: 250,
      render: (_, record) => (
        <Space>
          <img 
            src={record.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(record.nickname || '用户')}&background=random&size=40`}
            alt="avatar" 
            style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(record.nickname || '用户')}&background=random&size=40`;
            }}
          />
          <div>
            <div>{record.nickname || '未设置昵称'}</div>
            <div style={{ fontSize: 12, color: '#999' }}>{record.phone || '未绑定'}</div>
          </div>
        </Space>
      )
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (status) => getStatusTag(status)
    },
    {
      title: '违规次数',
      dataIndex: 'violation_count',
      width: 100,
      render: (count) => (
        <span style={{ color: count > 0 ? '#ff4d4f' : undefined }}>
          {count}
        </span>
      )
    },
    {
      title: '帖子数',
      dataIndex: 'post_count',
      width: 100
    },
    {
      title: '粉丝/关注',
      key: 'follow',
      width: 120,
      render: (_, record) => (
        <span>{record.followers_count} / {record.following_count}</span>
      )
    },
    {
      title: '注册时间',
      dataIndex: 'created_at',
      width: 180,
      render: (time) => dayjs(time).format('YYYY-MM-DD HH:mm')
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 280,
      render: (_, record) => (
        <Space>
          <Button 
            type="link" 
            size="small" 
            icon={<UserOutlined />}
            onClick={() => handleViewDetail(record.id)}
          >
            查看
          </Button>
          
          {record.status === 'normal' && (
            <Button 
              type="link" 
              size="small" 
              danger
              icon={<LockOutlined />}
              onClick={() => handleOpenBan(record.id)}
            >
              封禁
            </Button>
          )}
          
          {record.status === 'banned' && (
            <Popconfirm
              title="确认解封此用户？"
              onConfirm={() => handleUnban(record.id)}
              okText="确认"
              cancelText="取消"
            >
              <Button 
                type="link" 
                size="small"
                icon={<UnlockOutlined />}
              >
                解封
              </Button>
            </Popconfirm>
          )}
          
          {record.status !== 'deleted' && (
            <Popconfirm
              title="确认删除此用户？此操作不可恢复！"
              onConfirm={() => handleDelete(record.id)}
              okText="确认"
              cancelText="取消"
            >
              <Button 
                type="link" 
                size="small" 
                danger
                icon={<DeleteOutlined />}
              >
                删除
              </Button>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <h2>用户管理</h2>

      {/* 筛选区 */}
      <div style={{ marginBottom: 16, background: '#fff', padding: 16, borderRadius: 8 }}>
        <Space wrap>
          <Input
            placeholder="搜索昵称/手机号"
            value={filters.keyword}
            onChange={(e) => setFilters({ ...filters, keyword: e.target.value })}
            onPressEnter={handleSearch}
            style={{ width: 200 }}
            prefix={<SearchOutlined />}
          />
          
          <Select
            placeholder="用户状态"
            value={filters.status}
            onChange={(value) => setFilters({ ...filters, status: value })}
            style={{ width: 120 }}
            allowClear
          >
            <Option value="normal">正常</Option>
            <Option value="banned">已封禁</Option>
            <Option value="deleted">已删除</Option>
          </Select>

          <RangePicker
            value={filters.dateRange}
            onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
            placeholder={['开始日期', '结束日期']}
          />

          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
            搜索
          </Button>
          <Button onClick={handleReset}>重置</Button>
        </Space>
      </div>

      {/* 用户列表 */}
      <Table
        columns={columns}
        dataSource={users}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条`
        }}
        onChange={(newPagination) => loadUsers(newPagination.current)}
        scroll={{ x: 1200 }}
      />

      {/* 用户详情弹窗 */}
      <Modal
        title="用户详情"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={800}
      >
        {detailLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>加载中...</div>
        ) : currentUser ? (
          <div>
            {/* 基本信息 */}
            <div style={{ marginBottom: 24 }}>
              <h3>基本信息</h3>
              <div style={{ display: 'flex', gap: 16 }}>
                <img 
                  src={currentUser.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(currentUser.nickname || '用户')}&background=random&size=80`}
                  alt="avatar"
                  style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover' }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(currentUser.nickname || '用户')}&background=random&size=80`;
                  }}
                />
                <div style={{ flex: 1 }}>
                  <p><strong>昵称：</strong>{currentUser.nickname || '未设置'}</p>
                  <p><strong>手机号：</strong>{currentUser.phone || '未绑定'}</p>
                  <p><strong>状态：</strong>{getStatusTag(currentUser.status)}</p>
                  <p><strong>违规次数：</strong>{currentUser.violation_count}</p>
                </div>
                <div>
                  <p><strong>帖子数：</strong>{currentUser.post_count}</p>
                  <p><strong>收藏数：</strong>{currentUser.favorite_count}</p>
                  <p><strong>粉丝数：</strong>{currentUser.followers_count}</p>
                  <p><strong>关注数：</strong>{currentUser.following_count}</p>
                </div>
              </div>
            </div>

            {/* 最近帖子 */}
            {currentUser.recent_posts && currentUser.recent_posts.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <h3>最近帖子</h3>
                <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                  {currentUser.recent_posts.map(post => (
                    <div key={post.id} style={{ 
                      padding: 12, 
                      border: '1px solid #f0f0f0',
                      borderRadius: 4,
                      marginBottom: 8
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span><strong>{post.title}</strong></span>
                        <span style={{ fontSize: 12, color: '#999' }}>
                          {dayjs(post.created_at).format('YYYY-MM-DD HH:mm')}
                        </span>
                      </div>
                      <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                        点赞 {post.like_count} · 状态: {post.status}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 封禁记录 */}
            {currentUser.ban_history && currentUser.ban_history.length > 0 && (
              <div>
                <h3>封禁记录</h3>
                <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                  {currentUser.ban_history.map(ban => (
                    <div key={ban.id} style={{ 
                      padding: 12, 
                      border: '1px solid #f0f0f0',
                      borderRadius: 4,
                      marginBottom: 8
                    }}>
                      <div><strong>原因：</strong>{ban.reason}</div>
                      <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                        类型：{ban.ban_type === 'permanent' ? '永久' : `临时(${ban.ban_days}天)`} · 
                        操作人：{ban.admin_nickname || ban.admin_username} · 
                        状态：{ban.status}
                      </div>
                      <div style={{ fontSize: 12, color: '#999' }}>
                        {dayjs(ban.created_at).format('YYYY-MM-DD HH:mm')}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : null}
      </Modal>

      {/* 封禁用户弹窗 */}
      <Modal
        title="封禁用户"
        open={banVisible}
        onOk={handleBan}
        onCancel={() => {
          setBanVisible(false);
          banForm.resetFields();
        }}
        confirmLoading={banLoading}
        okText="确认封禁"
        cancelText="取消"
      >
        <Form
          form={banForm}
          layout="vertical"
        >
          <Form.Item
            label="封禁类型"
            name="banType"
            rules={[{ required: true, message: '请选择封禁类型' }]}
          >
            <Radio.Group>
              <Radio value="temporary">临时封禁</Radio>
              <Radio value="permanent">永久封禁</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => 
              prevValues.banType !== currentValues.banType
            }
          >
            {({ getFieldValue }) =>
              getFieldValue('banType') === 'temporary' ? (
                <Form.Item
                  label="封禁天数"
                  name="banDays"
                  rules={[{ required: true, message: '请输入封禁天数' }]}
                >
                  <InputNumber min={1} max={365} style={{ width: '100%' }} />
                </Form.Item>
              ) : null
            }
          </Form.Item>

          <Form.Item
            label="封禁原因"
            name="reason"
            rules={[{ required: true, message: '请输入封禁原因' }]}
          >
            <Input.TextArea 
              rows={4} 
              placeholder="请详细说明封禁原因"
              maxLength={200}
              showCount
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default Users;
